import { Fragment } from "react";
import cn from "classnames";
import { Dialog, Transition } from "@headlessui/react";
import Header from "./ModalHeader";
import Body from "./ModalBody";
import Footer from "./ModalFooter";
import { SolIcon } from "@solstice/sol-react";

export interface ModalProps {
  isOpen: boolean;
  setIsOpen: (a: boolean) => void;
  children: any;
  size?: "large" | "default" | "small" | "medium" | "extra" | "xl";
  className?: string;
  handlePrev?: any;
  handleNext?: any;
  isPrev?: boolean;
  isNext?: boolean;
}

function Modal<T extends object>({
  isOpen,
  setIsOpen,
  children,
  size = "default",
  className,
  handlePrev,
  handleNext,
  isNext,
  isPrev,
}: ModalProps) {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-30" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto z-30">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {handlePrev && (
              <button
                className={cn(
                  "mr-7 rounded-md bg-jll-surface-base-secondary p-3 w-[44px] h-[44px]",
                  {
                    invisible: !isPrev,
                  }
                )}
                onClick={(e) => {
                  e?.stopPropagation();
                  handlePrev();
                  setIsOpen(true);
                }}
              >
                <SolIcon
                  icon="chevron_left"
                  className="text-jll-text-base-default"
                  size="20"
                />
              </button>
            )}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              {/* transform */}
              <Dialog.Panel
                className={cn(
                  "relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:p-6",
                  {
                    "sm:max-w-md": size === "default",
                    "sm:max-w-xl": size === "xl",
                    "sm:max-w-2xl": size === "medium",
                    "sm:max-w-4xl": size === "large",
                    "sm:max-w-6xl": size === "extra",
                    "sm:max-w-xs": size === "small",
                  },
                  className
                )}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
            {handleNext && (
              <button
                className={cn(
                  "ml-7 rounded-md bg-jll-surface-base-secondary p-3 w-[44px] h-[44px]",
                  {
                    invisible: !isNext,
                  }
                )}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext();
                  setIsOpen(true);
                }}
              >
                <SolIcon
                  icon="chevron_right"
                  className="text-jll-text-base-default"
                  size="20"
                />
              </button>
            )}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
